import React, { useCallback, useState } from "react";
import carArt from "../img/car-art.svg";

/*
 * CarDamageSelector
 * -----------------
 * Interactive wrapper around the designer's top-down car diagram.
 *
 * SOURCE OF TRUTH: src/img/car-base.svg is the designer's file, kept untouched.
 * It contains two top-level groups:
 *
 *   <g id="car">   the static body art (19 nodes)
 *   <g id="icon">  15 named hotspots, each holding BOTH states:
 *                    <g id="base.section">     white disc + black "+"
 *                    <g id="onclick.section">  pink glow + white disc + pink "-"
 *
 * Both states ship visible in the file, which is why every "+" in the raw SVG
 * sits inside a pink halo -- the onclick disc (r=41.4) is LARGER than the base
 * disc (r=39.3), so it is never covered. The states are meant to be swapped,
 * not stacked.
 *
 * So the marker layer is not rendered from the file at all. src/img/car-art.svg
 * is car-base.svg with <g id="icon"> removed -- body art only -- and the hotspots
 * are rebuilt here as React, with exactly one state drawn at a time.
 *
 * Each section carried transform="translate(145,180)"; that offset is folded
 * into the cx/cy below, so CAR_SECTIONS is in plain viewBox coordinates.
 *
 * If the designer ships a new SVG: replace car-base.svg, then regenerate
 * car-art.svg by deleting the <g id="icon"> ... </g> block, and re-check the
 * centres below if the hotspots moved.
 */

/** viewBox of car-base.svg / car-art.svg. */
const ART_WIDTH = 790.1;
const ART_HEIGHT = 1646.6;

/** Radius of the invisible click target. Just outside the disc; nearest pair is 150 apart. */
const HIT_RADIUS = 44;

/** The only marker colour in the designer's file (classes .st9-.st13). */
const ACCENT = "#FF3B7C";

/**
 * Rotating the whole <svg> would rotate the marker symbols too, and a rotated
 * "-" reads as a vertical bar. So only the car art is rotated; the hotspot
 * centres are mapped into the rotated space by `place()` and the symbols
 * themselves are drawn upright.
 */
function getRotation(rotation) {
  switch (((rotation % 360) + 360) % 360) {
    case 90: // clockwise -- front of the car ends up on the right
      return {
        width: ART_HEIGHT,
        height: ART_WIDTH,
        artTransform: `translate(${ART_HEIGHT} 0) rotate(90)`,
        place: (x, y) => [ART_HEIGHT - y, x],
      };
    case 180:
      return {
        width: ART_WIDTH,
        height: ART_HEIGHT,
        artTransform: `translate(${ART_WIDTH} ${ART_HEIGHT}) rotate(180)`,
        place: (x, y) => [ART_WIDTH - x, ART_HEIGHT - y],
      };
    case 270: // anti-clockwise -- front of the car ends up on the left
      return {
        width: ART_HEIGHT,
        height: ART_WIDTH,
        artTransform: `translate(0 ${ART_WIDTH}) rotate(-90)`,
        place: (x, y) => [y, ART_WIDTH - x],
      };
    default:
      return {
        width: ART_WIDTH,
        height: ART_HEIGHT,
        artTransform: null,
        place: (x, y) => [x, y],
      };
  }
}

/**
 * The 15 hotspots, ids taken verbatim from the group names in car-base.svg,
 * ordered front to back. Left/right are the car's own sides: this is a
 * top-down view with the front at the top, so the car's left is on the
 * viewer's left at rotation 0.
 */
export const CAR_SECTIONS = [
  { id: "front_bumper", label: "Front Bumper", cx: 392.1, cy: 121.3 },
  { id: "left_front_bumper", label: "Left Front Bumper", cx: 163.2, cy: 215.1 },
  { id: "right_front_bumper", label: "Right Front Bumper", cx: 618.2, cy: 215.1 },
  { id: "hood", label: "Hood", cx: 399.7, cy: 330.2 },
  { id: "left_mirror", label: "Left Mirror", cx: 128.3, cy: 582.7 },
  { id: "right_mirror", label: "Right Mirror", cx: 682.1, cy: 574.7 },
  { id: "front_glass", label: "Windshield", cx: 395.4, cy: 551.2 },
  { id: "roof", label: "Roof", cx: 396.1, cy: 846.4 },
  { id: "left_qtr", label: "Left Quarter Panel", cx: 115.5, cy: 996.7 },
  { id: "right_qtr", label: "Right Quarter Panel", cx: 679.2, cy: 993.1 },
  { id: "rear_glass", label: "Rear Glass", cx: 396.8, cy: 1141.7 },
  { id: "tail_gate", label: "Tailgate", cx: 400.3, cy: 1328.2 },
  { id: "rear_left_bumper", label: "Rear Left Bumper", cx: 137.4, cy: 1424.2 },
  { id: "rear_right_bumper", label: "Rear Right Bumper", cx: 628.5, cy: 1424.2 },
  { id: "rear_bumper", label: "Rear Bumper", cx: 399.8, cy: 1478.4 },
];

/** Kept so `import { CAR_MARKERS }` from older code keeps working. */
export const CAR_MARKERS = CAR_SECTIONS;

const STYLES = `
  .cds-hotspot { cursor: pointer; outline: none; }
  .cds-ring { opacity: 0; transition: opacity .12s ease-out; }
  .cds-hotspot:hover .cds-ring { opacity: .5; }
  .cds-hotspot:focus-visible .cds-ring { opacity: 1; }
`;

/**
 * Unselected state -- the `base.section` group, re-centred on (0, 0).
 * White disc with a black "+". Geometry copied from the designer's file.
 */
function BaseMarker() {
  return (
    <g>
      <circle r={39.3} fill="#FFFFFF" stroke="#000000" strokeWidth={1.0408} />
      <line x1={0} y1={-19.2} x2={0} y2={24.4} fill="none" stroke="#000000" strokeWidth={1.5} />
      <line x1={-25.8} y1={1.7} x2={24.7} y2={1.7} fill="none" stroke="#000000" strokeWidth={1.5} />
    </g>
  );
}

/**
 * Selected state -- the `onclick.section` group, re-centred on (0, 0).
 * Three stacked translucent haloes, then a white disc and the "-".
 * Drawn smallest halo first, exactly as in the source file.
 */
function SelectedMarker() {
  return (
    <g>
      <circle r={61.1} fill={ACCENT} opacity={0.12} />
      <circle r={95.9} fill={ACCENT} opacity={0.1} />
      <circle r={135.2} fill={ACCENT} opacity={0.04} />
      <circle r={41.4} fill="#FFFFFF" stroke={ACCENT} strokeWidth={2.1804} />
      <line x1={-21.7} y1={0} x2={23} y2={0} fill="none" stroke={ACCENT} strokeWidth={4} />
    </g>
  );
}

function Hotspot({ section, x, y, isSelected, onToggle }) {
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onToggle(section, event);
      }
    },
    [section, onToggle]
  );

  return (
    <g
      className="cds-hotspot"
      transform={`translate(${x} ${y})`}
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      aria-label={section.label}
      onClick={(event) => onToggle(section, event)}
      onKeyDown={handleKeyDown}
    >
      {/* Exactly one state is ever drawn -- never both. */}
      {isSelected ? <SelectedMarker /> : <BaseMarker />}

      <circle className="cds-ring" r={HIT_RADIUS} fill="none" stroke={ACCENT} strokeWidth={3} />
      {/* Transparent hit target, kept last so it sits above the artwork. */}
      <circle r={HIT_RADIUS} fill="transparent" />
    </g>
  );
}

/**
 * @param {object}   props
 * @param {string[]} [props.selectedIds]        Controlled selection. Omit for internal state.
 * @param {string[]} [props.defaultSelectedIds] Initial selection when uncontrolled.
 * @param {function} [props.onMarkerClick]      (section, isNowSelected, nextSelectedIds) => void
 * @param {function} [props.onSelectionChange]  (nextSelectedIds) => void
 * @param {number}   [props.rotation]           0 | 90 | 180 | 270. 90 puts the front on
 *                                              the right, 270 puts it on the left.
 * @param {number}   [props.maxWidth]           Cap in px. Default 820, which lands the
 *                                              rotated diagram at roughly 820x394.
 */
export default function CarDamageSelector({
  selectedIds,
  defaultSelectedIds = [],
  onMarkerClick,
  onSelectionChange,
  rotation = 90,
  maxWidth = 820,
  className,
  style,
  ...svgProps
}) {
  const [internalSelected, setInternalSelected] = useState(defaultSelectedIds);

  const isControlled = selectedIds !== undefined;
  const selected = isControlled ? selectedIds : internalSelected;

  const handleToggle = useCallback(
    (section) => {
      const isNowSelected = !selected.includes(section.id);
      const next = isNowSelected
        ? [...selected, section.id]
        : selected.filter((id) => id !== section.id);

      if (!isControlled) setInternalSelected(next);
      if (onMarkerClick) onMarkerClick(section, isNowSelected, next);
      if (onSelectionChange) onSelectionChange(next);
    },
    [selected, isControlled, onMarkerClick, onSelectionChange]
  );

  const view = getRotation(rotation);

  return (
    <svg
      viewBox={`0 0 ${view.width} ${view.height}`}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{
        display: "block",
        width: "100%",
        maxWidth,
        height: "auto",
        ...style,
      }}
      {...svgProps}
    >
      <style>{STYLES}</style>

      {/* Body art only -- no hotspots baked in. pointerEvents:none so it never
          swallows a click. */}
      <g transform={view.artTransform || undefined}>
        <image
          href={carArt}
          x={0}
          y={0}
          width={ART_WIDTH}
          height={ART_HEIGHT}
          style={{ pointerEvents: "none" }}
        />
      </g>

      {/* Hotspots sit outside the rotated group so the +/- stay upright. */}
      {CAR_SECTIONS.map((section) => {
        const [x, y] = view.place(section.cx, section.cy);
        return (
          <Hotspot
            key={section.id}
            section={section}
            x={x}
            y={y}
            isSelected={selected.includes(section.id)}
            onToggle={handleToggle}
          />
        );
      })}
    </svg>
  );
}
